import type { ReactNode } from 'react';
import { Icon } from '../common/Icon';

export interface EmptyStateProps {
  /** Material Symbol icon name shown in the soft circle. */
  icon: string;
  /** Headline — short, declarative ("No conversations yet"). */
  title: ReactNode;
  /** Optional secondary line — describe what the user could do next. */
  description?: ReactNode;
  /** Optional call-to-action button row, rendered below the description. */
  action?: ReactNode;
}

/**
 * Standard empty state for any list/grid/table that has no items.
 * Replaces the dozens of hand-rolled `<div className="text-center
 * py-16">` blocks scattered through pages.
 *
 * @example
 * <EmptyState
 *   icon="forum"
 *   title={t('conversations.empty')}
 *   description={t('conversations.emptyHint')}
 *   action={<Button onClick={openCreate}>New conversation</Button>}
 * />
 */
export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-8 py-20">
      <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-white/[0.04] flex items-center justify-center mb-4">
        <Icon name={icon} size={28} className="text-gray-300 dark:text-gray-600" />
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">{title}</p>
      {description && (
        <p className="text-xs text-gray-400 dark:text-gray-500 max-w-[260px]">
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
